import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { financialAPI } from '@/lib/api-client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ChevronRight, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react'

type SegmentType = 'OBRA' | 'OPERACIONAL' | 'RECEITA'

interface Subcategory {
  id: string
  name: string
  isActive: boolean
  groupId: string
}

interface Group {
  id: string
  name: string
  type: SegmentType
  isActive: boolean
  subcategories: Subcategory[]
}

// ── Nó de subcategoria (folha) ──

interface SubcategoryRowProps {
  sub: Subcategory
  onToggle: (id: string, isActive: boolean) => void
  onDelete: (sub: Subcategory) => void
}

function SubcategoryRow({ sub, onToggle, onDelete }: SubcategoryRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 pl-8 pr-3 py-2 border-l-2 border-neutral-100 ml-3">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className={`text-sm ${sub.isActive ? 'text-neutral-800' : 'text-neutral-400 line-through'}`}>
          {sub.name}
        </span>
        {!sub.isActive && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-neutral-400">
            Inativa
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Checkbox
          checked={sub.isActive}
          onChange={(e) => onToggle(sub.id, (e.target as HTMLInputElement).checked)}
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-neutral-400 hover:text-red-600"
          onClick={() => onDelete(sub)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

// ── Nó de grupo (com subcategorias dentro) ──

interface GroupNodeProps {
  group: Group
  onToggleGroup: (id: string, isActive: boolean) => void
  onDeleteGroup: (group: Group) => void
  onAddSubcategory: (groupId: string, name: string) => Promise<void>
  onToggleSub: (id: string, isActive: boolean) => void
  onDeleteSub: (sub: Subcategory) => void
  isCreatingSub: boolean
}

function GroupNode({
  group,
  onToggleGroup,
  onDeleteGroup,
  onAddSubcategory,
  onToggleSub,
  onDeleteSub,
  isCreatingSub,
}: GroupNodeProps) {
  const [expanded, setExpanded] = useState(true)
  const [newSubName, setNewSubName] = useState('')

  const handleAddSub = async () => {
    const name = newSubName.trim()
    if (!name) return
    await onAddSubcategory(group.id, name)
    setNewSubName('')
  }

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-neutral-50">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <ChevronRight
            className={`h-4 w-4 text-neutral-500 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
          <span className={`text-sm font-medium ${group.isActive ? 'text-neutral-900' : 'text-neutral-400 line-through'}`}>
            {group.name}
          </span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-neutral-500">
            {group.subcategories.length} subcategoria{group.subcategories.length === 1 ? '' : 's'}
          </Badge>
          {!group.isActive && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-neutral-400">
              Inativa
            </Badge>
          )}
        </button>
        <div className="flex items-center gap-3">
          <Checkbox
            checked={group.isActive}
            onChange={(e) => onToggleGroup(group.id, (e.target as HTMLInputElement).checked)}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-neutral-400 hover:text-red-600"
            onClick={() => onDeleteGroup(group)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="py-1">
          {group.subcategories.length === 0 ? (
            <p className="text-xs text-neutral-400 px-3 py-3 pl-11">Nenhuma subcategoria.</p>
          ) : (
            group.subcategories.map((sub) => (
              <SubcategoryRow
                key={sub.id}
                sub={sub}
                onToggle={onToggleSub}
                onDelete={onDeleteSub}
              />
            ))
          )}

          <div className="flex items-center gap-2 px-3 py-2 pl-11">
            <Input
              placeholder="Nova subcategoria..."
              value={newSubName}
              onChange={(e) => setNewSubName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSub()}
              className="h-8 text-sm"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddSub}
              disabled={!newSubName.trim() || isCreatingSub}
              className="h-8"
            >
              {isCreatingSub ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Card por segmento ──

interface SegmentCardProps {
  title: string
  description: string
  type: SegmentType
}

function SegmentCard({ title, description, type }: SegmentCardProps) {
  const queryClient = useQueryClient()
  const [newGroupName, setNewGroupName] = useState('')
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null)
  const [deletingSub, setDeletingSub] = useState<Subcategory | null>(null)

  const queryKey = ['financial-category-groups', type]

  const { data: groups, isLoading } = useQuery<Group[]>({
    queryKey,
    queryFn: () => financialAPI.listCategoryGroups(type) as Promise<Group[]>,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const createGroupMutation = useMutation({
    mutationFn: (name: string) => financialAPI.createCategoryGroup({ name, type }),
    onSuccess: () => {
      toast.success('Categoria adicionada')
      setNewGroupName('')
      invalidate()
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { isActive?: boolean } }) =>
      financialAPI.updateCategoryGroup(id, data),
    onSuccess: invalidate,
    onError: (err: Error) => toast.error(err.message),
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (id: string) => financialAPI.deleteCategoryGroup(id),
    onSuccess: () => {
      toast.success('Categoria e subcategorias removidas')
      invalidate()
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const createSubMutation = useMutation({
    mutationFn: ({ groupId, name }: { groupId: string; name: string }) =>
      financialAPI.createFinancialCategory({ name, groupId }),
    onSuccess: () => {
      toast.success('Subcategoria adicionada')
      invalidate()
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const updateSubMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { isActive?: boolean } }) =>
      financialAPI.updateFinancialCategory(id, data),
    onSuccess: invalidate,
    onError: (err: Error) => toast.error(err.message),
  })

  const deleteSubMutation = useMutation({
    mutationFn: (id: string) => financialAPI.deleteFinancialCategory(id),
    onSuccess: () => {
      toast.success('Subcategoria removida')
      invalidate()
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const handleAddGroup = () => {
    const name = newGroupName.trim()
    if (name) createGroupMutation.mutate(name)
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-sm text-neutral-500">{description}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : !groups || groups.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-4">Nenhuma categoria cadastrada.</p>
        ) : (
          <div className="space-y-2">
            {groups.map((group) => (
              <GroupNode
                key={group.id}
                group={group}
                onToggleGroup={(id, isActive) => updateGroupMutation.mutate({ id, data: { isActive } })}
                onDeleteGroup={(g) => setDeletingGroup(g)}
                onAddSubcategory={async (groupId, name) => {
                  await createSubMutation.mutateAsync({ groupId, name })
                }}
                onToggleSub={(id, isActive) => updateSubMutation.mutate({ id, data: { isActive } })}
                onDeleteSub={(sub) => setDeletingSub(sub)}
                isCreatingSub={createSubMutation.isPending}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Input
            placeholder="Nova categoria..."
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
          />
          <Button
            size="sm"
            onClick={handleAddGroup}
            disabled={!newGroupName.trim() || createGroupMutation.isPending}
          >
            {createGroupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Adicionar
          </Button>
        </div>
      </CardContent>

      <Dialog open={!!deletingGroup} onOpenChange={(open) => !open && setDeletingGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover categoria?</DialogTitle>
            <DialogDescription>
              {deletingGroup && deletingGroup.subcategories.length > 0 ? (
                <>
                  A categoria <strong>{deletingGroup.name}</strong> tem{' '}
                  <strong>{deletingGroup.subcategories.length} subcategoria{deletingGroup.subcategories.length === 1 ? '' : 's'}</strong>{' '}
                  que ser{deletingGroup.subcategories.length === 1 ? 'á removida' : 'ão removidas'} junto.
                  <br /><br />
                  Transações já classificadas não serão afetadas (mantêm o nome como histórico).
                </>
              ) : (
                <>
                  Tem certeza que deseja remover a categoria <strong>{deletingGroup?.name}</strong>?
                  <br /><br />
                  Esta ação não pode ser desfeita.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingGroup(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deletingGroup) deleteGroupMutation.mutate(deletingGroup.id)
                setDeletingGroup(null)
              }}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletingSub} onOpenChange={(open) => !open && setDeletingSub(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover subcategoria?</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover a subcategoria <strong>{deletingSub?.name}</strong>?
              <br /><br />
              Transações já classificadas não serão afetadas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingSub(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deletingSub) deleteSubMutation.mutate(deletingSub.id)
                setDeletingSub(null)
              }}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// ── Componente principal ──

export function ExpenseCategorySettings() {
  const queryClient = useQueryClient()

  const seedMutation = useMutation({
    mutationFn: () => financialAPI.seedFinancialCategories(),
    onSuccess: () => {
      toast.success('Categorias padrão restauradas')
      queryClient.invalidateQueries({ queryKey: ['financial-category-groups'] })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Categorias Financeiras</h2>
          <p className="text-sm text-neutral-500">
            Configure as categorias e subcategorias usadas na classificação de receitas e despesas.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => seedMutation.mutate()}
          disabled={seedMutation.isPending}
        >
          {seedMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="mr-2 h-4 w-4" />
          )}
          Restaurar Padrão
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SegmentCard
          title="Receitas"
          description="Categorias e subcategorias para receitas."
          type="RECEITA"
        />
        <SegmentCard
          title="Despesas de Obra"
          description="Categorias e subcategorias para despesas de projeto."
          type="OBRA"
        />
        <SegmentCard
          title="Despesas Operacionais"
          description="Categorias e subcategorias para despesas gerais da empresa."
          type="OPERACIONAL"
        />
      </div>
    </div>
  )
}
